function normalizeOrigin(value) {
  const input = String(value || "").trim();
  if (!input) return "";
  try {
    return new URL(input).origin;
  } catch {
    return "";
  }
}

function normalizeProductionYourBarrioOrigin(origin) {
  const normalized = normalizeOrigin(origin);
  if (!normalized) return "";
  try {
    const url = new URL(normalized);
    if (isLocalHost(url.hostname)) return normalized;
    if (
      url.hostname === "yourbarrio.com" ||
      url.hostname === "www.yourbarrio.com"
    ) {
      return "https://www.yourbarrio.com";
    }
  } catch {
    return normalized;
  }
  return normalized;
}

function isLocalHost(hostname) {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost")
  );
}

function shouldUseConfiguredCanonical(currentOrigin, configuredOrigin) {
  if (!currentOrigin || !configuredOrigin) return false;
  try {
    const current = new URL(currentOrigin);
    const configured = new URL(configuredOrigin);
    if (isLocalHost(current.hostname)) return false;
    return (
      current.hostname === configured.hostname ||
      current.hostname === "yourbarrio.com" ||
      current.hostname === "www.yourbarrio.com"
    );
  } catch {
    return false;
  }
}

function getYourBarrioCanonicalOrigin(currentOrigin) {
  try {
    const current = new URL(currentOrigin);
    if (isLocalHost(current.hostname)) return "";
    if (
      current.hostname === "yourbarrio.com" ||
      current.hostname === "www.yourbarrio.com"
    ) {
      return "https://www.yourbarrio.com";
    }
  } catch {
    // fall through
  }
  return "";
}

function getOriginCategory(value) {
  const normalized = normalizeOrigin(value);
  if (!normalized) return "missing";
  try {
    const { hostname } = new URL(normalized);
    if (hostname === "www.yourbarrio.com") return "canonical";
    if (hostname === "yourbarrio.com") return "apex_redirect";
    if (isLocalHost(hostname)) return "local";
    if (hostname.endsWith(".vercel.app")) return "preview";
    return "other";
  } catch {
    return "invalid";
  }
}

function getRouteCategory(pathname) {
  const firstSegment = String(pathname || "")
    .split("/")
    .filter(Boolean)[0];
  return firstSegment || "root";
}

// Host canonicalization must stay aligned with Supabase cookie scope in
// lib/authCookies.js and the final redirect origin in app/api/auth/callback.
export function getOAuthRedirectOrigin(currentOrigin) {
  const detectedOrigin = normalizeProductionYourBarrioOrigin(currentOrigin);
  const configuredOrigin = normalizeProductionYourBarrioOrigin(
    process.env.NEXT_PUBLIC_SITE_URL || process.env.SITE_URL || ""
  );
  if (shouldUseConfiguredCanonical(detectedOrigin, configuredOrigin)) {
    return configuredOrigin;
  }
  return (
    getYourBarrioCanonicalOrigin(detectedOrigin) ||
    detectedOrigin ||
    configuredOrigin ||
    "http://localhost:3000"
  );
}

// All Google OAuth entry points must use this helper. Hand-built callback URLs
// can split hosts from cookies and recreate the "signed in, returned as guest" bug.
export function buildOAuthCallbackUrl({ currentOrigin, next, intent, loginIntentId } = {}) {
  const redirectOrigin = getOAuthRedirectOrigin(currentOrigin);
  const callback = new URL("/api/auth/callback", redirectOrigin);
  if (typeof next === "string" && next.trim()) {
    callback.searchParams.set("next", next);
  }
  if (intent === "business" || intent === "customer") {
    callback.searchParams.set("intent", intent);
  }
  if (/^[a-zA-Z0-9-]{12,128}$/.test(String(loginIntentId || ""))) {
    callback.searchParams.set("yb_login_intent", loginIntentId);
  }
  return callback.toString();
}

export function getOAuthConfigDiagnostics({ currentOrigin } = {}) {
  const detectedOrigin = normalizeProductionYourBarrioOrigin(currentOrigin);
  const configuredSiteUrl = normalizeProductionYourBarrioOrigin(
    process.env.NEXT_PUBLIC_SITE_URL || process.env.SITE_URL || ""
  );
  const redirectOrigin = getOAuthRedirectOrigin(detectedOrigin);
  const callback = new URL("/api/auth/callback", redirectOrigin);
  return {
    hasSupabaseUrl: Boolean(process.env.NEXT_PUBLIC_SUPABASE_URL),
    hasSupabaseAnonKey: Boolean(process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY),
    configuredSiteUrlPresent: Boolean(configuredSiteUrl),
    configuredOriginCategory: getOriginCategory(configuredSiteUrl),
    detectedOriginCategory: getOriginCategory(detectedOrigin),
    redirectOriginCategory: getOriginCategory(redirectOrigin),
    callbackRouteCategory: getRouteCategory(callback.pathname),
    callbackSearchKeys: Array.from(callback.searchParams.keys()),
  };
}

/**
 * @param {{
 *   provider?: string;
 *   redirectTo?: string;
 *   currentOrigin?: string;
 * }} options
 */
export function logOAuthStart({
  provider = "google",
  redirectTo,
  currentOrigin,
} = {}) {
  if (typeof window === "undefined") return;
  if (
    process.env.NODE_ENV === "production" &&
    process.env.NEXT_PUBLIC_AUTH_DIAG !== "1"
  ) {
    return;
  }
  let redirectRouteCategory = null;
  let redirectSearchKeys = [];
  try {
    const redirectUrl = new URL(
      redirectTo || "/api/auth/callback",
      currentOrigin || window.location.origin
    );
    redirectRouteCategory = getRouteCategory(redirectUrl.pathname);
    redirectSearchKeys = Array.from(redirectUrl.searchParams.keys());
  } catch {
    // Keep diagnostics structural when a malformed URL is supplied.
  }
  console.info("[AUTH_OAUTH_START]", {
    provider,
    redirectRouteCategory,
    redirectSearchKeys,
    detectedOriginCategory: getOriginCategory(
      currentOrigin || window.location.origin
    ),
    config: getOAuthConfigDiagnostics({ currentOrigin }),
  });
}
